import { clientEnvModule } from "@/modules/client-env";
import { ThemeType } from "@/mds/themes/theme-context";
import {
  CalendarEvent,
  DesktopAudioArtifactUploadFailureDebugInfo,
  DesktopAudioArtifactRef,
  DesktopAudioArtifactSnapshot,
  DesktopRemoveAudioArtifactArgs,
  DesktopAudioRecordingRef,
  DesktopAudioRecordingStrategy,
  DesktopAudioUploadCompletedEvent,
  DesktopAudioUploadErrorEvent,
  DesktopAudioUploadProgressEvent,
  DesktopAudioWaveformUpdateEvent,
  DesktopRecordingStartAttemptTrace,
  DesktopRecordingRecoveryState,
  DesktopRecordingSession,
  RecallMeetingDetectedEventKind,
  RecallPermissionKind,
  RecallPermissionStatus,
  ElectronAppInfo,
  RecallSdkStatePayload,
  ImportAppleNotesResult,
  GetAppleNoteContentResult,
  MenuActionEvent,
  DesktopShortcutEvent,
  OAuthAccessTokenGetRequestArgs,
  OAuthAccessTokenGetResponseArgs,
  OAuthAccessTokenSetArgs,
  AiToolkitRequestArgs,
  AiToolkitResponseArgs,
  WorkingContextMatch,
  WorkingContextAddSubscriptionArgs,
  WorkingContextRemoveSubscriptionArgs,
  WorkingContextSnapshot,
  WorkingContextStatus,
  StartDesktopAudioUploadArgs,
  StartDesktopAudioUploadResult,
  StartRecordingOutcome,
} from "@/modules/electron/types";
import { TrackedEvent } from "@/domains/metrics";

/**
 * Should match the definition in our `mem-client-desktop` project.
 */
type ElectronAPI = {
  navigateToInternalUrl: (args: { url: string }) => Promise<void>;
  navigateToExternalUrl: (args: { url: string }) => Promise<void>;
  toggleMenu: () => Promise<void>;
  minimizeWindow: () => Promise<void>;
  maximizeWindow: () => Promise<void>;
  closeWindow: () => Promise<void>;
  // Passing tells the handler to keep checking for updates, even if there isn't one available yet.
  updateNow: (waitForUpdate: boolean) => Promise<boolean>;
  restartApp?: () => Promise<void>;
  openSystemSoundSettings?: (args?: { focus?: "input" | "output" }) => Promise<void>;
  setTheme: (args: { theme: ThemeType }) => Promise<void>;
  setCalendarNotificationEnabled: (args: { enabled: boolean }) => Promise<void>;
  getCalendarNotificationEnabled: () => Promise<{ enabled: boolean }>;
  setMeetingNotificationEnabled: (args: { enabled: boolean }) => Promise<void>;
  getMeetingNotificationEnabled: () => Promise<{ enabled: boolean }>;
  setTeamsMeetingDetectionEnabled: (args: { enabled: boolean }) => Promise<void>;
  getTeamsMeetingDetectionEnabled: () => Promise<{ enabled: boolean }>;
  setAdvancedMeetingDetectionShown: (args: { shown: boolean }) => Promise<void>;
  getAdvancedMeetingDetectionShown: () => Promise<{ shown: boolean }>;
  setClientMetadata?: (args: { context: Record<string, unknown> }) => Promise<void>;
  clearClientMetadata?: () => Promise<void>;
  oauthAccessToken?: {
    set: (args: OAuthAccessTokenSetArgs) => Promise<void>;
    clear: () => Promise<void>;
    onGetRequested?: (cb: (args: OAuthAccessTokenGetRequestArgs) => void) => () => void;
    respondToGetRequest?: (args: OAuthAccessTokenGetResponseArgs) => Promise<void>;
    onRefreshRequested?: (cb: () => void) => () => void;
  };
  aiToolkit?: {
    onRequest?: (cb: (args: AiToolkitRequestArgs) => void) => () => void;
    respondToRequest?: (args: AiToolkitResponseArgs) => Promise<void>;
  };
  getElectronAppInfo?: () => Promise<ElectronAppInfo>;
  onBrowserMetadata?: (
    cb: (metadata: {
      segmentAnonymousId?: string;
      gaClientId?: string;
      gaSessionId?: string;
    }) => void
  ) => Promise<() => void>;
  setCalendarEvents?: (args: {
    ongoingEvents: CalendarEvent[];
    upcomingEvents: CalendarEvent[];
  }) => Promise<void>;
  onMenuActionEvent?: (cb: (payload: { actionEvent: MenuActionEvent }) => void) => () => void;
  onDesktopShortcutEvent?: (
    cb: (payload: { shortcutEvent: DesktopShortcutEvent }) => void
  ) => () => void;
  onTrackedEvent?: (cb: (payload: { trackedEvent: TrackedEvent }) => void) => () => void;
  setIsClientReady?: (args: { isReady: boolean }) => Promise<void>;
  importAppleNotes?: (options?: {
    collectAttachments?: boolean;
  }) => Promise<ImportAppleNotesResult>;
  getAppleNoteContent?: (noteId: number) => Promise<GetAppleNoteContentResult>;
  closeAppleNotesReader?: () => Promise<void>;
  resetTccAndClearCache?: (args: { appName: string }) => Promise<void>;
  /**
   * Events related to the Recall.ai Desktop SDK
   */
  recall: {
    prepareDesktopRecording: () => Promise<{ windowId: string }>;
    startRecording: (args: {
      audioRecordingId: string;
      windowId: string;
      uploadToken: string;
      recordingStrategy: DesktopAudioRecordingStrategy;
      calendarEventId?: string;
      startAttemptTrace?: DesktopRecordingStartAttemptTrace;
    }) => Promise<StartRecordingOutcome>;
    pause: (args: { windowId: string }) => Promise<void>;
    resume: (args: { windowId: string }) => Promise<void>;
    stop: (args: { windowId: string }) => Promise<void>;
    getRecordingSession: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopRecordingSession | null>;
    getRecordingRecoveryState: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopRecordingRecoveryState | null>;
    pauseRecordingSession: (args: DesktopAudioRecordingRef) => Promise<void>;
    resumeRecordingSession: (args: DesktopAudioRecordingRef) => Promise<void>;
    stopRecordingSession: (args: DesktopAudioRecordingRef) => Promise<void>;
    onEvent?: (cb: (payload: { event: unknown }) => void) => () => void;
    onMeetingEvent?: (
      cb: (payload: {
        event: {
          window: { id: string; title?: string; url?: string; platform: string };
          info?: {
            startTime?: string | null;
            endTime?: string | null;
            attendees?: string[] | null;
          };
        };
        kind: RecallMeetingDetectedEventKind;
        debug: boolean;
      }) => void
    ) => () => void;
    requestPermission: (permission: RecallPermissionKind) => Promise<void>;
    getPermissionStatus: () => Promise<Record<RecallPermissionKind, RecallPermissionStatus>>;
    onPermissionStatus?: (
      cb: (payload: {
        event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
      }) => void
    ) => () => void;
    openPermissionPane: (permission: RecallPermissionKind) => Promise<void>;
    getSdkState: () => Promise<RecallSdkStatePayload>;
    onSdkState?: (cb: (payload: RecallSdkStatePayload) => void) => () => void;
    testUnexpectedShutdown: () => Promise<void>;
  };
  audioArtifacts: {
    getAudioArtifactRef: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactRef | null>;
    getAudioArtifactSnapshot: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactSnapshot | null>;
    getAudioArtifactUploadFailureDebugInfo: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactUploadFailureDebugInfo | null>;
    finalizeAudioArtifact: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactRef | null>;
    removeAudioArtifact: (args: DesktopRemoveAudioArtifactArgs) => Promise<void>;
  };
  audioUpload: {
    startAudioUpload: (args: StartDesktopAudioUploadArgs) => Promise<StartDesktopAudioUploadResult>;
    onProgress?: (cb: (payload: DesktopAudioUploadProgressEvent) => void) => () => void;
    onCompleted?: (cb: (payload: DesktopAudioUploadCompletedEvent) => void) => () => void;
    onError?: (cb: (payload: DesktopAudioUploadErrorEvent) => void) => () => void;
  };
  audioWaveform: {
    onUpdate?: (cb: (payload: DesktopAudioWaveformUpdateEvent) => void) => () => void;
  };
  workingContext?: {
    getStatus: () => Promise<WorkingContextStatus>;
    getSnapshot: () => Promise<WorkingContextSnapshot>;
    addSubscription: (args: WorkingContextAddSubscriptionArgs) => Promise<WorkingContextSnapshot>;
    removeSubscription: (
      args: WorkingContextRemoveSubscriptionArgs
    ) => Promise<WorkingContextSnapshot>;
    onMatch?: (cb: (payload: WorkingContextMatch) => void) => () => void;
  };
};

export const getElectronAPI = (): ElectronAPI | null => {
  const isDesktop = clientEnvModule.isDesktop();

  if (isDesktop) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).electronAPI as ElectronAPI;
  }

  return null;
};
